import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  BookOpen,
  Download,
  Loader2,
  Lock,
  ChevronLeft,
  Sparkles,
  ArrowRight,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchLibrary, downloadEbook, fetchEntitlements, API } from "@/lib/api";
import UserBadge from "@/components/UserBadge";

// Workbook chapters in narrative order — used to render one "shelf" per chapter
// so the library visually mirrors the workbook regardless of which ebooks the
// author has published.
const CHAPTER_SHELVES = [
  { id: "inner-child",      number: 1, name: "The Inner Child" },
  { id: "core-wounds",      number: 2, name: "The Core Wound" },
  { id: "limiting-beliefs", number: 3, name: "The Limiting Belief" },
  { id: "shadow-traits",    number: 4, name: "The Shadow Trait" },
  { id: "triggers",         number: 5, name: "The Trigger" },
  { id: "forgiveness",      number: 6, name: "The Forgiveness" },
  { id: "integration",      number: 7, name: "The Integration" },
];

const UNGATED_TIERS = new Set([
  "admin",
  "listener_lifetime",
  "grandfathered_lifetime",
  "attending_quarterly",
  "umbra_companion_yearly",
  "monthly_promo",
  "monthly_standard",
]);

// Curated leather-binding palette — one entry per series number so each shelf
// has a distinct spine color the way a real bookshelf does. Pulled from
// classic bookbinding: oxblood, bottle green, indigo, cognac, aged gold,
// burgundy, slate, warm ivory (preface).
const SPINE_PALETTE = {
  1: { color: "#7a1e1e", highlight: "rgba(255, 220, 190, 0.16)" }, // oxblood
  2: { color: "#1e3a2a", highlight: "rgba(200, 230, 210, 0.14)" }, // bottle green
  3: { color: "#1a2648", highlight: "rgba(200, 210, 240, 0.14)" }, // indigo
  4: { color: "#6b3a1a", highlight: "rgba(255, 220, 180, 0.16)" }, // cognac
  5: { color: "#7a5a1c", highlight: "rgba(255, 230, 170, 0.18)" }, // aged gold
  6: { color: "#4a1a2b", highlight: "rgba(240, 200, 210, 0.14)" }, // burgundy
  7: { color: "#2a3648", highlight: "rgba(200, 220, 240, 0.14)" }, // slate
  8: { color: "#8a6a3a", highlight: "rgba(255, 235, 200, 0.22)" }, // ivory-gold
};

function spineStyle(seriesNumber) {
  const p = SPINE_PALETTE[seriesNumber] || SPINE_PALETTE[1];
  return {
    "--spine-color": p.color,
    "--spine-highlight": p.highlight,
  };
}

function daysRemaining(iso) {
  if (!iso) return 0;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
}

async function startBookCheckout(bookId) {
  const res = await fetch(`${API}/library/${bookId}/checkout`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ origin_url: window.location.origin }),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail?.detail || `Checkout failed (${res.status})`);
  }
  const data = await res.json();
  if (data.checkout_url) window.location.href = data.checkout_url;
}

export default function Library() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [buying, setBuying] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setData(await fetchLibrary());
    } catch {
      toast.error("Could not load the library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    // Success message when returning from a Stripe checkout
    const params = new URLSearchParams(window.location.search);
    if (params.get("purchased")) {
      toast.success("Payment received. Your book and chapter are unlocking.");
      // Clean the URL so a refresh doesn't re-fire the toast
      window.history.replaceState({}, "", "/library");
    }
  }, []);

  // Book credit reminder toast — fires ONCE per session so revisits don't
  // nag. Uses sessionStorage; cleared when the tab closes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("umbra:book-credit-toast-shown") === "1") return;
    let cancelled = false;
    (async () => {
      try {
        const ent = await fetchEntitlements();
        if (cancelled) return;
        const cents = Number(ent?.book_credit_cents || 0);
        if (cents <= 0) return;
        const expiresAt = ent.book_credit_expires_at;
        const days = expiresAt
          ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (24 * 3600 * 1000)))
          : null;
        const daysLabel = days === null ? "" : days === 0 ? " · expires today" : days === 1 ? " · 1 day left" : ` · ${days} days left`;
        toast.info(`$${(cents / 100).toFixed(2)} book credit${daysLabel}`, {
          description: "Applied automatically when you upgrade to any subscription.",
          duration: 8000,
          action: {
            label: "See tiers",
            onClick: () => { window.location.href = "/pricing"; },
          },
        });
        sessionStorage.setItem("umbra:book-credit-toast-shown", "1");
      } catch {
        /* silent — the pricing-page nudge is the durable surface */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const download = async (book) => {
    try {
      setDownloading(book.id);
      await downloadEbook(book.id, book.title);
      toast.success(`Saved "${book.title}"`);
      await load();
    } catch (e) {
      const reason = e?.response?.data?.detail?.reason;
      if (reason === "quota_reached") {
        toast.error("You've hit your download cap for this period.");
      } else if (reason === "upgrade_required" || e?.response?.status === 402) {
        toast.error("This ebook is for paid tiers. Upgrade to unlock.");
      } else {
        toast.error("Download failed");
      }
    } finally {
      setDownloading(null);
    }
  };

  const buy = async (book) => {
    try {
      setBuying(book.id);
      await startBookCheckout(book.id);
    } catch (e) {
      toast.error(e.message || "Could not open checkout");
      setBuying(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-bone-400">
        <Loader2 className="h-5 w-5 animate-spin mr-3" /> loading
      </div>
    );
  }

  const { ebooks = [], tier = "free", quota } = data || {};
  const bookBySection = new Map();
  const prefaceBooks = [];
  for (const b of ebooks) {
    if (b.section_id) bookBySection.set(b.section_id, b);
    else if (b.series_number === 8 || b.is_lead_magnet) prefaceBooks.push(b);
  }

  return (
    <div className="min-h-screen bg-obsidian-900">
      <header className="px-4 sm:px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 min-h-[44px] -mx-2 px-2 sm:min-h-0 sm:mx-0 sm:px-0 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100"
        >
          <ChevronLeft className="h-3 w-3" /> Workbook
        </Link>
        <UserBadge />
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 pt-10 sm:pt-12 pb-24">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen className="h-5 w-5 text-terracotta" />
          <p
            className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta"
            data-testid="library-eyebrow"
          >
            The Library · seven shelves
          </p>
        </div>
        <h1
          className="font-serif text-3xl sm:text-4xl md:text-5xl text-bone-100 mb-3 leading-tight"
          data-testid="library-title"
        >
          One shelf for every chapter
        </h1>
        <p className="font-serif italic text-bone-400 max-w-2xl mb-8 leading-relaxed">
          Each shelf holds the book that opens that chapter of the workbook.
          Buy a book for <strong className="text-bone-200 not-italic">$5</strong> and the matching chapter unlocks for
          <strong className="text-bone-200 not-italic"> 10 days</strong>. Books open in sequence — Book 2 waits until you've held Book 1.
        </p>

        <div
          className="inline-flex items-center gap-3 border border-obsidian-600 bg-obsidian-800/40 px-4 py-2 mb-12"
          data-testid="library-quota"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${quota?.unlimited ? "bg-emerald-400" : "bg-terracotta"}`} />
          <span className="font-sans text-[0.7rem] uppercase tracking-[0.24em] text-bone-300">
            Tier: {tier}
            {quota?.unlimited ? " · unlimited downloads" : ""}
          </span>
        </div>

        {/* MOBILE — tap-friendly card grid (below md) */}
        <div className="space-y-16 md:hidden" data-testid="library-shelves-mobile">
          {prefaceBooks.length > 0 && (
            <section data-testid="shelf-preface-mobile" className="relative">
              <div className="flex items-baseline justify-between mb-5 border-b border-obsidian-600 pb-3">
                <div>
                  <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-1">
                    Start here · free with your account
                  </p>
                  <h2 className="font-serif text-2xl text-bone-100">The Preface</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6">
                {prefaceBooks.map((book) => (
                  <ChapterBookCard
                    key={book.id}
                    book={book}
                    tier={tier}
                    downloading={downloading === book.id}
                    buying={false}
                    onDownload={() => download(book)}
                    onBuy={() => {}}
                  />
                ))}
              </div>
            </section>
          )}
          {CHAPTER_SHELVES.map((shelf) => {
            const book = bookBySection.get(shelf.id);
            return (
              <section key={shelf.id} data-testid={`shelf-${shelf.id}-mobile`} className="relative">
                <div className="flex items-baseline justify-between mb-5 border-b border-obsidian-600 pb-3">
                  <div>
                    <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-1">
                      Chapter {shelf.number}
                    </p>
                    <h2 className="font-serif text-2xl text-bone-100">{shelf.name}</h2>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  {book ? (
                    <ChapterBookCard
                      book={book}
                      tier={tier}
                      downloading={downloading === book.id}
                      buying={buying === book.id}
                      onDownload={() => download(book)}
                      onBuy={() => buy(book)}
                    />
                  ) : (
                    <ComingSoonTile chapterNumber={shelf.number} chapterName={shelf.name} />
                  )}
                </div>
              </section>
            );
          })}
        </div>

        {/* DESKTOP — walnut bookcase view (md+) */}
        <div className="hidden md:block" data-testid="library-shelves">
          <div className="library-bookcase relative">
            <div className="library-chandelier" aria-hidden="true" />
            {/* Preface shelf */}
            {prefaceBooks.length > 0 && (
              <BookcaseShelf
                pillarLabel="PREFACE"
                chapterTitle="Start here · free"
                books={prefaceBooks}
                tier={tier}
                onDownload={download}
                onBuy={buy}
                downloading={downloading}
                buying={buying}
                testId="shelf-preface"
              />
            )}
            {/* Seven chapter shelves */}
            {CHAPTER_SHELVES.map((shelf) => {
              const book = bookBySection.get(shelf.id);
              return (
                <BookcaseShelf
                  key={shelf.id}
                  pillarLabel={`CHAPTER ${romanize(shelf.number)}`}
                  chapterTitle={shelf.name}
                  books={book ? [book] : []}
                  ghostChapter={!book ? shelf : null}
                  tier={tier}
                  onDownload={download}
                  onBuy={buy}
                  downloading={downloading}
                  buying={buying}
                  testId={`shelf-${shelf.id}`}
                />
              );
            })}
          </div>
          <p className="text-center mt-8 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500">
            Hover a spine to pull the book off the shelf
          </p>
        </div>
      </div>
    </div>
  );
}

function ChapterBookCard({ book, tier, onDownload, onBuy, downloading, buying }) {
  const owned = book.is_owned;
  const isFreeTier = book.free_tier || book.is_lead_magnet;
  const seqLocked = !book.sequential_unlocked;
  const trialActive = Boolean(book.chapter_trial_until);
  const trialDays = daysRemaining(book.chapter_trial_until);
  const privileged = UNGATED_TIERS.has(tier);
  const price = ((book.price_cents || 0) / 100).toFixed(2);
  // Downloadable branch: any free-tier lead magnet, ownership, or privileged user
  const canDownload = privileged || owned || isFreeTier;

  return (
    <div
      data-testid={`ebook-card-${book.id}`}
      className="library-book border border-obsidian-600 bg-obsidian-800/30 p-5 sm:p-6 flex flex-col hover:border-terracotta transition-colors relative"
    >
      <div className="aspect-[3/4] bg-obsidian-900 mb-4 border border-obsidian-600 overflow-hidden flex items-center justify-center relative">
        {book.cover_path ? (
          <img
            src={`${API}/library/${book.id}/cover`}
            alt={book.title}
            className={`w-full h-full object-cover ${seqLocked && !privileged ? "opacity-40" : ""}`}
          />
        ) : (
          <BookOpen className={`h-10 w-10 text-bone-500 ${seqLocked && !privileged ? "opacity-40" : ""}`} />
        )}
        {trialActive && (
          <div className="absolute top-2 left-2 bg-terracotta/95 text-obsidian-900 px-2 py-1 flex items-center gap-1" data-testid={`ebook-trial-${book.id}`}>
            <Clock className="h-3 w-3" />
            <span className="font-sans text-[0.55rem] uppercase tracking-[0.2em] font-semibold">
              {trialDays}d chapter access
            </span>
          </div>
        )}
        {seqLocked && !privileged && !isFreeTier && (
          <div className="absolute top-2 right-2 bg-obsidian-900/85 border border-terracotta/60 px-2 py-1 flex items-center gap-1">
            <Lock className="h-3 w-3 text-terracotta" />
            <span className="font-sans text-[0.55rem] uppercase tracking-[0.2em] text-terracotta">
              Sequential
            </span>
          </div>
        )}
        {isFreeTier && !privileged && (
          <div className="absolute top-2 right-2 bg-terracotta text-obsidian-900 px-2 py-1">
            <span className="font-sans text-[0.55rem] uppercase tracking-[0.2em] font-semibold">
              Free
            </span>
          </div>
        )}
      </div>
      <h3 className="font-serif text-xl text-bone-100 mb-1 leading-tight">{book.title}</h3>
      {book.subtitle && (
        <p className="font-serif italic text-bone-400 text-sm mb-3">{book.subtitle}</p>
      )}
      {book.description && (
        <p className="font-sans text-sm text-bone-300 leading-relaxed mb-4 flex-1">
          {book.description}
        </p>
      )}

      {/* CTA */}
      {canDownload ? (
        <Button
          onClick={onDownload}
          disabled={downloading}
          data-testid={`ebook-download-${book.id}`}
          className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-5 mt-auto min-h-[48px]"
        >
          {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          {downloading ? "Downloading" : isFreeTier && !privileged && !owned ? "Download · free" : owned ? "Download · yours" : "Download"}
        </Button>
      ) : seqLocked ? (
        <div className="mt-auto space-y-3">
          <div className="border border-obsidian-600 bg-obsidian-900/60 p-4 text-center">
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-bone-500 mb-2">
              Book {book.series_number} opens after Book {book.series_number - 1}
            </p>
            <p className="font-sans text-[0.7rem] text-bone-500">
              Buy the previous book first, or unlock this chapter through a subscription.
            </p>
          </div>
          <Link
            to="/pricing"
            data-testid={`ebook-upgrade-${book.id}`}
            className="inline-flex items-center justify-center w-full gap-2 rounded-none border border-obsidian-600 hover:border-terracotta text-bone-300 hover:text-terracotta font-sans uppercase tracking-wider text-xs py-4 min-h-[48px] transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            See subscription tiers
          </Link>
        </div>
      ) : (
        <div className="mt-auto space-y-2">
          <Button
            onClick={onBuy}
            disabled={buying}
            data-testid={`ebook-buy-${book.id}`}
            className="w-full rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-5 min-h-[48px]"
          >
            {buying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Buy ${price} · 10-day chapter access
          </Button>
          <p className="text-center font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500">
            Book stays yours · chapter unlocks for 10 days
          </p>
        </div>
      )}
    </div>
  );
}

function ComingSoonTile({ chapterNumber, chapterName }) {
  return (
    <div
      data-testid={`shelf-empty-${chapterNumber}`}
      className="border border-dashed border-obsidian-600 bg-obsidian-800/20 p-5 sm:p-6 flex flex-col items-center justify-center text-center min-h-[380px]"
    >
      <div className="h-12 w-12 border border-obsidian-600 flex items-center justify-center mb-4">
        <BookOpen className="h-5 w-5 text-bone-500" />
      </div>
      <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-bone-500 mb-2">
        Coming soon
      </p>
      <h3 className="font-serif text-xl text-bone-200 mb-2 leading-tight">
        Book {chapterNumber} · {chapterName}
      </h3>
      <p className="font-sans text-sm text-bone-500 leading-relaxed max-w-xs">
        Available on subscription tiers until this book ships.
      </p>
      <Link
        to="/pricing"
        className="mt-5 inline-flex items-center gap-2 min-h-[44px] px-4 font-sans text-xs uppercase tracking-[0.2em] text-terracotta hover:text-terracotta-deep border-b border-terracotta"
      >
        See tiers <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

function romanize(n) {
  const map = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII"];
  return map[n] || String(n);
}

// Filler-book palette — deeper, more muted than the anchor spines so real
// books stand out. Warm bookbinding tones only.
const ATMOSPHERE_PALETTE = [
  "#3a2818", "#2a1810", "#4a2818", "#3a1e14", "#5a3a20", "#2a2018",
  "#442218", "#3a2c1c", "#5a3018", "#2c1e14", "#4a3226", "#38221a",
];

// Deterministic pseudo-random atmosphere layout keyed on chapter id so the
// shelves stay stable between renders (no re-shuffle on hover).
function atmosphereBooks(seed, count) {
  const books = [];
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) >>> 0;
  const rand = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xFFFFFFFF;
  };
  for (let i = 0; i < count; i++) {
    books.push({
      width: 18 + Math.floor(rand() * 22),        // 18-40 px
      height: 190 + Math.floor(rand() * 40),      // 190-230 px
      color: ATMOSPHERE_PALETTE[Math.floor(rand() * ATMOSPHERE_PALETTE.length)],
      lean: rand() < 0.15,                        // 15% chance of a leaner
    });
  }
  return books;
}

/**
 * A single shelf inside the walnut bookcase. Renders a vertical chapter
 * label on the left pillar and 1..N book spines lined up along the wood
 * plank. Empty chapters render a ghost spine + "Coming soon" copy.
 */
function BookcaseShelf({
  pillarLabel,
  chapterTitle,
  books,
  ghostChapter,
  tier,
  onDownload,
  onBuy,
  downloading,
  buying,
  testId,
}) {
  // Atmosphere books fill the shelf so it feels lived-in. Split them so the
  // real book sits roughly in the middle, with more books to its right than
  // left (imitates a shelf that grows over time).
  const seedKey = testId || pillarLabel || "shelf";
  const leftFillers = atmosphereBooks(seedKey + "-L", 3);
  const rightFillers = atmosphereBooks(seedKey + "-R", 18);
  return (
    <div className="mb-8 last:mb-0" data-testid={testId}>
      {/* Victorian arch that crowns the shelf */}
      <div className="library-shelf-arch ml-[48px]" aria-hidden="true" />
      <div className="flex items-stretch">
        {/* Left pillar with chapter label */}
        <div className="flex flex-col items-center justify-end pr-3 pb-4 min-w-[36px] text-[0.7rem]">
          <span className="library-shelf-pillar">{pillarLabel}</span>
        </div>
        {/* The shelf row itself */}
        <div className="library-shelf-row flex-1">
          {leftFillers.map((a, i) => (
            <div
              key={`Lf-${i}`}
              className={`library-atmosphere-spine ${a.lean ? "library-atmosphere-spine--lean" : ""}`}
              style={{ "--atm-width": `${a.width}px`, "--atm-height": `${a.height}px`, "--atm-color": a.color }}
              aria-hidden="true"
            />
          ))}
          {books.map((book) => (
            <BookSpine
              key={book.id}
              book={book}
              tier={tier}
              onDownload={onDownload}
              onBuy={onBuy}
              downloading={downloading === book.id}
              buying={buying === book.id}
            />
          ))}
          {ghostChapter && (
            <GhostSpine chapterNumber={ghostChapter.number} chapterName={ghostChapter.name} />
          )}
          {rightFillers.map((a, i) => (
            <div
              key={`Rf-${i}`}
              className={`library-atmosphere-spine ${a.lean ? "library-atmosphere-spine--lean" : ""}`}
              style={{ "--atm-width": `${a.width}px`, "--atm-height": `${a.height}px`, "--atm-color": a.color }}
              aria-hidden="true"
            />
          ))}
        </div>
      </div>
      {/* Chapter subtitle displayed below the shelf */}
      <p className="ml-[48px] mt-2 font-sans text-[0.7rem] uppercase tracking-[0.22em] text-bone-500">
        <span className="text-bone-300">{chapterTitle}</span>
      </p>
    </div>
  );
}

/**
 * Individual leather-bound book spine. Renders as a vertical strip with a
 * gold-stamped title. On hover, the whole element tilts up and a floating
 * cover card appears above, showing the actual cover art + CTA.
 */
function BookSpine({ book, tier, onDownload, onBuy, downloading, buying }) {
  const owned = book.is_owned;
  const isFreeTier = book.free_tier || book.is_lead_magnet;
  const seqLocked = !book.sequential_unlocked;
  const trialActive = Boolean(book.chapter_trial_until);
  const trialDays = daysRemaining(book.chapter_trial_until);
  const privileged = UNGATED_TIERS.has(tier);
  const canDownload = privileged || owned || isFreeTier;
  const locked = seqLocked && !privileged && !isFreeTier;
  const price = ((book.price_cents || 0) / 100).toFixed(2);
  const style = spineStyle(book.series_number);

  return (
    <div
      data-testid={`spine-${book.id}`}
      className={`library-book-spine ${locked ? "library-book-spine--locked" : ""}`}
      style={style}
      tabIndex={0}
      aria-label={`${book.title}${locked ? ` — locked, opens after Book ${book.series_number - 1}` : ""}`}
    >
      <span className="library-book-spine__title">{book.title}</span>
      {locked && (
        <span className="absolute top-1 left-1/2 -translate-x-1/2 text-terracotta">
          <Lock className="h-3 w-3" />
        </span>
      )}

      {/* Floating cover card */}
      <div className="library-book-cover" data-testid={`cover-${book.id}`}>
        {book.cover_path ? (
          <div className="aspect-[3/4] w-full bg-obsidian-900 border border-obsidian-600 mb-3 overflow-hidden">
            <img
              src={`${API}/library/${book.id}/cover`}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[3/4] w-full bg-obsidian-900 border border-obsidian-600 mb-3 flex items-center justify-center">
            <BookOpen className="h-8 w-8 text-bone-500" />
          </div>
        )}
        <p className="font-sans text-[0.6rem] uppercase tracking-[0.2em] text-terracotta mb-1">
          {book.subtitle || `Book ${book.series_number || ""} · The Umbra Series`}
        </p>
        <h3 className="font-serif text-base text-bone-100 leading-tight mb-2">{book.title}</h3>
        {trialActive && (
          <p className="font-sans text-[0.6rem] uppercase tracking-[0.18em] text-terracotta mb-2 flex items-center gap-1">
            <Clock className="h-3 w-3" /> {trialDays}d chapter access
          </p>
        )}

        {/* CTA */}
        {canDownload ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onDownload(book);
            }}
            disabled={downloading}
            data-testid={`spine-download-${book.id}`}
            className="w-full inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans text-[0.65rem] uppercase tracking-[0.2em] px-3 py-3 min-h-[44px] transition-colors disabled:opacity-60"
          >
            {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            {isFreeTier && !privileged && !owned ? "Download · free" : owned ? "Yours" : "Download"}
          </button>
        ) : locked ? (
          <div className="space-y-2">
            <p className="font-sans text-[0.6rem] uppercase tracking-[0.18em] text-bone-500 leading-relaxed text-center">
              Opens after Book {book.series_number - 1}
            </p>
            <Link
              to="/pricing"
              className="w-full inline-flex items-center justify-center gap-2 border border-obsidian-600 hover:border-terracotta text-bone-300 hover:text-terracotta font-sans text-[0.6rem] uppercase tracking-[0.2em] px-3 py-2 min-h-[36px] transition-colors"
            >
              <Sparkles className="h-3 w-3" /> Subscribe
            </Link>
          </div>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onBuy(book);
            }}
            disabled={buying}
            data-testid={`spine-buy-${book.id}`}
            className="w-full inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans text-[0.65rem] uppercase tracking-[0.18em] px-3 py-3 min-h-[44px] transition-colors disabled:opacity-60"
          >
            {buying ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            Buy ${price} · 10-day access
          </button>
        )}
      </div>
    </div>
  );
}

function GhostSpine({ chapterNumber, chapterName }) {
  return (
    <div
      className="library-book-spine library-book-spine--ghost"
      data-testid={`spine-ghost-${chapterNumber}`}
      aria-label={`Book ${chapterNumber} · ${chapterName} — coming soon`}
    >
      <span className="library-book-spine__title text-bone-500/60">Coming soon</span>
    </div>
  );
}

